import React, { useCallback, useEffect, useState } from "react";
import { api } from "./apiClient";
import { formatApiError } from "./apiError";
import { useAuth } from "./AuthContext";
import { useServerDebug } from "./DebugContext";
import "./adminDashboard.css";

function asArray(v) {
  return Array.isArray(v) ? v : [];
}

export default function AdminDashboard({ onBack, tab = "users", onTabChange }) {
  const { user, logout, hasPermission } = useAuth();
  const serverDebug = useServerDebug();
  const [err, setErr] = useState(null);

  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);

  const [newUser, setNewUser] = useState({
    email: "",
    username: "",
    password: "",
    first_name: "",
    last_name: "",
    role_id: "",
    is_staff: false,
    is_superuser: false,
  });
  const [importMsg, setImportMsg] = useState(null);
  const [cuPortalRows, setCuPortalRows] = useState([]);
  const [cuImportMsg, setCuImportMsg] = useState(null);
  const [newRole, setNewRole] = useState({ name: "", description: "" });
  const [newPerm, setNewPerm] = useState({ code: "", description: "" });

  const [rolePermEdit, setRolePermEdit] = useState(null);
  const [rolePermSelected, setRolePermSelected] = useState(new Set());

  const loadUsers = useCallback(async () => {
    const { data } = await api.get("/admin/users");
    setUsers(asArray(data));
  }, []);
  const loadRoles = useCallback(async () => {
    const { data } = await api.get("/admin/roles");
    setRoles(asArray(data));
  }, []);
  const loadPermissions = useCallback(async () => {
    const { data } = await api.get("/admin/permissions");
    setPermissions(asArray(data));
  }, []);
  const loadCuPortalCredentials = useCallback(async () => {
    const { data } = await api.get("/admin/cu-portal-credentials");
    setCuPortalRows(asArray(data));
  }, []);

  const refresh = useCallback(async () => {
    setErr(null);
    try {
      if (hasPermission("admin.users")) {
        await loadUsers();
        await loadCuPortalCredentials();
      }
      if (hasPermission("admin.roles")) await loadRoles();
      if (hasPermission("admin.permissions")) await loadPermissions();
    } catch (e) {
      setErr(formatApiError(e, serverDebug));
    }
  }, [
    hasPermission,
    loadUsers,
    loadCuPortalCredentials,
    loadRoles,
    loadPermissions,
  ]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openRolePerms = async (role) => {
    setErr(null);
    try {
      const { data } = await api.get(`/admin/roles/${role.id}/permissions`);
      setRolePermEdit(role);
      setRolePermSelected(new Set(asArray(data).map((p) => p.id)));
    } catch (e) {
      setErr(formatApiError(e, serverDebug));
    }
  };

  const saveRolePerms = async () => {
    if (!rolePermEdit) return;
    setErr(null);
    try {
      await api.put(`/admin/roles/${rolePermEdit.id}/permissions`, {
        permission_ids: Array.from(rolePermSelected),
      });
      setRolePermEdit(null);
    } catch (e) {
      setErr(formatApiError(e, serverDebug));
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    setErr(null);
    try {
      await api.post("/admin/users", {
        email: newUser.email.trim(),
        username: newUser.username.trim(),
        password: newUser.password,
        first_name: newUser.first_name.trim(),
        last_name: newUser.last_name.trim(),
        role_id: newUser.role_id ? Number(newUser.role_id) : null,
        is_staff: newUser.is_staff,
        is_superuser: newUser.is_superuser,
      });
      setNewUser({
        email: "",
        username: "",
        password: "",
        first_name: "",
        last_name: "",
        role_id: "",
        is_staff: false,
        is_superuser: false,
      });
      await loadUsers();
    } catch (e) {
      setErr(formatApiError(e, serverDebug));
    }
  };

  const importCuPortalCsv = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setErr(null);
    setCuImportMsg(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/cu-portal-credentials/import-csv", fd);
      setCuImportMsg(
        `CU portal rows upserted: ${data.upserted ?? 0}. ` +
          (data.errors?.length ? `${data.errors.length} row errors.` : ""),
      );
      if (data.errors?.length) {
        setErr(data.errors.slice(0, 5).join("\n"));
      }
      await loadCuPortalCredentials();
    } catch (err) {
      setErr(formatApiError(err, serverDebug));
    }
  };

  const importUsersCsv = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setErr(null);
    setImportMsg(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/users/import-csv", fd);
      setImportMsg(
        `Inserted ${data.inserted}, updated ${data.updated}. ` +
          (data.errors?.length ? `Errors: ${data.errors.length}` : "No row errors."),
      );
      if (data.errors?.length) {
        setErr(data.errors.slice(0, 5).join("\n"));
      }
      await loadUsers();
    } catch (err) {
      setErr(formatApiError(err, serverDebug));
    }
  };

  const createRole = async (e) => {
    e.preventDefault();
    setErr(null);
    try {
      await api.post("/admin/roles", newRole);
      setNewRole({ name: "", description: "" });
      await loadRoles();
    } catch (e) {
      setErr(formatApiError(e, serverDebug));
    }
  };

  const createPerm = async (e) => {
    e.preventDefault();
    setErr(null);
    try {
      await api.post("/admin/permissions", newPerm);
      setNewPerm({ code: "", description: "" });
      await loadPermissions();
    } catch (e) {
      setErr(formatApiError(e, serverDebug));
    }
  };

  const currentTab = tab || "users";
  const setTab = (nextTab) => {
    if (typeof onTabChange === "function") {
      onTabChange(nextTab);
    }
  };

  return (
    <div className="adm-root">
      <aside className="adm-side">
        <h1>Administration</h1>
        <p className="adm-muted">Users, roles, and permissions (finance_db).</p>
        <button type="button" className="adm-back" onClick={onBack}>
          ← Dashboard
        </button>
        <nav className="adm-nav">
          {hasPermission("admin.users") && (
            <button
              type="button"
              className={currentTab === "users" ? "adm-nav-btn active" : "adm-nav-btn"}
              onClick={() => setTab("users")}
            >
              Users
            </button>
          )}
          {hasPermission("admin.roles") && (
            <button
              type="button"
              className={currentTab === "roles" ? "adm-nav-btn active" : "adm-nav-btn"}
              onClick={() => setTab("roles")}
            >
              Roles
            </button>
          )}
          {hasPermission("admin.permissions") && (
            <button
              type="button"
              className={currentTab === "permissions" ? "adm-nav-btn active" : "adm-nav-btn"}
              onClick={() => setTab("permissions")}
            >
              Permissions
            </button>
          )}
        </nav>
        <p className="adm-user">
          {user?.username ? `${user.username} · ${user.email}` : user?.email}
        </p>
        <button type="button" className="adm-back" onClick={logout}>
          Sign out
        </button>
      </aside>

      <main className="adm-main">
        {err && <div className="adm-error">{String(err)}</div>}

        {currentTab === "users" && hasPermission("admin.users") && (
          <section className="adm-panel">
            <h2>Users</h2>
            <div className="adm-form-row" style={{ marginBottom: 12 }}>
              <label className="adm-muted" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                Import Django-style CSV
                <input type="file" accept=".csv,text/csv" onChange={importUsersCsv} />
              </label>
            </div>
            {importMsg && <p className="adm-muted">{importMsg}</p>}
            <div className="adm-form-row" style={{ marginBottom: 12, flexWrap: "wrap" }}>
              <span className="adm-muted">
                Kohl CU sheet (save Excel as CSV). Headers: Credit Union, Model Code, Manager
                UserName, Manager Password, Employee UserName, Employee Password, Code User Name,
                Code Password — Django hashes and plain text both supported.
              </span>
              <label className="adm-muted" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                Import CU portal CSV
                <input type="file" accept=".csv,text/csv" onChange={importCuPortalCsv} />
              </label>
            </div>
            {cuImportMsg && <p className="adm-muted">{cuImportMsg}</p>}
            {cuPortalRows.length > 0 && (
              <div className="adm-table-wrap" style={{ marginBottom: 16 }}>
                <table className="adm-table">
                  <thead>
                    <tr>
                      <th>Credit union</th>
                      <th>Model</th>
                      <th>Manager user</th>
                      <th>Employee user</th>
                      <th>Code user</th>
                      <th>Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cuPortalRows.map((r) => (
                      <tr key={r.id}>
                        <td>{r.credit_union}</td>
                        <td>{r.model_code || "—"}</td>
                        <td>{r.manager_username || "—"}</td>
                        <td>{r.employee_username || "—"}</td>
                        <td>{r.code_username || "—"}</td>
                        <td className="adm-muted-sm">
                          {r.updated_at ? String(r.updated_at).slice(0, 19) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <form className="adm-form-row" onSubmit={createUser}>
              <input
                placeholder="Email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                required
              />
              <input
                placeholder="Username"
                value={newUser.username}
                onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                required
              />
              <input
                type="password"
                placeholder="Password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                required
              />
              <input
                placeholder="First name"
                value={newUser.first_name}
                onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })}
              />
              <input
                placeholder="Last name"
                value={newUser.last_name}
                onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })}
              />
              <select
                value={newUser.role_id}
                onChange={(e) => setNewUser({ ...newUser, role_id: e.target.value })}
              >
                <option value="">Role (auto if superuser/staff)</option>
                {roles.map((r) => (
                  <option key={r.id} value={String(r.id)}>
                    {r.name}
                  </option>
                ))}
              </select>
              <label className="adm-muted" style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <input
                  type="checkbox"
                  checked={newUser.is_staff}
                  onChange={(e) => setNewUser({ ...newUser, is_staff: e.target.checked })}
                />
                Staff
              </label>
              <label className="adm-muted" style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <input
                  type="checkbox"
                  checked={newUser.is_superuser}
                  onChange={(e) => setNewUser({ ...newUser, is_superuser: e.target.checked })}
                />
                Superuser
              </label>
              <button type="submit" className="adm-primary">
                Add user
              </button>
            </form>
            <div className="adm-table-wrap">
              <table className="adm-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Username</th>
                    <th>Name</th>
                    <th>Role</th>
                    <th>Staff</th>
                    <th>Super</th>
                    <th>Active</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <UserRow
                      key={u.id}
                      u={u}
                      roles={roles}
                      onSaved={loadUsers}
                      onError={setErr}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {currentTab === "roles" && hasPermission("admin.roles") && (
          <section className="adm-panel">
            <h2>Roles</h2>
            <form className="adm-form-row" onSubmit={createRole}>
              <input
                placeholder="Name"
                value={newRole.name}
                onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
                required
              />
              <input
                placeholder="Description"
                value={newRole.description}
                onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
              />
              <button type="submit" className="adm-primary">
                Add role
              </button>
            </form>
            <div className="adm-table-wrap">
              <table className="adm-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Description</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {roles.map((r) => (
                    <RoleRow key={r.id} r={r} onSaved={loadRoles} onError={setErr} onEditPerms={openRolePerms} />
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {currentTab === "permissions" && hasPermission("admin.permissions") && (
          <section className="adm-panel">
            <h2>Permissions</h2>
            <form className="adm-form-row" onSubmit={createPerm}>
              <input
                placeholder="Code (e.g. finance.view)"
                value={newPerm.code}
                onChange={(e) => setNewPerm({ ...newPerm, code: e.target.value })}
                required
              />
              <input
                placeholder="Description"
                value={newPerm.description}
                onChange={(e) => setNewPerm({ ...newPerm, description: e.target.value })}
              />
              <button type="submit" className="adm-primary">
                Add permission
              </button>
            </form>
            <div className="adm-table-wrap">
              <table className="adm-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Description</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {permissions.map((p) => (
                    <PermRow key={p.id} p={p} onSaved={loadPermissions} onError={setErr} />
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {rolePermEdit && (
          <div className="adm-modal-backdrop">
            <div className="adm-modal">
              <h3>Permissions for {rolePermEdit.name}</h3>
              <div className="adm-check-list">
                {permissions.map((p) => (
                  <label key={p.id} className="adm-check">
                    <input
                      type="checkbox"
                      checked={rolePermSelected.has(p.id)}
                      onChange={(e) => {
                        const next = new Set(rolePermSelected);
                        if (e.target.checked) next.add(p.id);
                        else next.delete(p.id);
                        setRolePermSelected(next);
                      }}
                    />
                    <span>{p.code}</span>
                    <span className="adm-muted-sm">{p.description}</span>
                  </label>
                ))}
              </div>
              <div className="adm-modal-actions">
                <button type="button" className="adm-primary" onClick={saveRolePerms}>
                  Save
                </button>
                <button type="button" className="adm-ghost" onClick={() => setRolePermEdit(null)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function UserRow({ u, roles, onSaved, onError }) {
  const serverDebug = useServerDebug();
  const [edit, setEdit] = useState(false);
  const [email, setEmail] = useState(u.email);
  const [username, setUsername] = useState(u.username || "");
  const [firstName, setFirstName] = useState(u.first_name || "");
  const [lastName, setLastName] = useState(u.last_name || "");
  const [fullName, setFullName] = useState(u.full_name || "");
  const [roleId, setRoleId] = useState(u.role_id != null ? String(u.role_id) : "");
  const [active, setActive] = useState(u.is_active);
  const [isStaff, setIsStaff] = useState(!!u.is_staff);
  const [isSuper, setIsSuper] = useState(!!u.is_superuser);
  const [password, setPassword] = useState("");

  const save = async () => {
    onError(null);
    try {
      const body = {
        email,
        username,
        first_name: firstName,
        last_name: lastName,
        full_name: fullName,
        role_id: roleId ? Number(roleId) : null,
        is_active: active,
        is_staff: isStaff,
        is_superuser: isSuper,
      };
      if (password) body.password = password;
      await api.patch(`/admin/users/${u.id}`, body);
      setEdit(false);
      setPassword("");
      await onSaved();
    } catch (e) {
      onError(formatApiError(e, serverDebug));
    }
  };

  const del = async () => {
    if (!window.confirm(`Delete user ${u.email}?`)) return;
    onError(null);
    try {
      await api.delete(`/admin/users/${u.id}`);
      await onSaved();
    } catch (e) {
      onError(formatApiError(e, serverDebug));
    }
  };

  if (!edit) {
    return (
      <tr>
        <td>{u.email}</td>
        <td>{u.username}</td>
        <td>{u.full_name || `${u.first_name || ""} ${u.last_name || ""}`.trim() || "—"}</td>
        <td>{roles.find((r) => r.id === u.role_id)?.name || "—"}</td>
        <td>{u.is_staff ? "yes" : "no"}</td>
        <td>{u.is_superuser ? "yes" : "no"}</td>
        <td>{u.is_active ? "yes" : "no"}</td>
        <td>
          <button type="button" className="adm-link" onClick={() => setEdit(true)}>
            Edit
          </button>{" "}
          <button type="button" className="adm-link danger" onClick={del}>
            Delete
          </button>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td>
        <input value={email} onChange={(e) => setEmail(e.target.value)} />
      </td>
      <td>
        <input value={username} onChange={(e) => setUsername(e.target.value)} />
      </td>
      <td>
        <input
          placeholder="First"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          style={{ width: "4.5rem" }}
        />
        <input
          placeholder="Last"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          style={{ width: "4.5rem" }}
        />
        <input
          placeholder="Display name"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
        />
      </td>
      <td>
        <select value={roleId} onChange={(e) => setRoleId(e.target.value)}>
          <option value="">No role</option>
          {roles.map((r) => (
            <option key={r.id} value={String(r.id)}>
              {r.name}
            </option>
          ))}
        </select>
      </td>
      <td>
        <label>
          <input type="checkbox" checked={isStaff} onChange={(e) => setIsStaff(e.target.checked)} />
        </label>
      </td>
      <td>
        <label>
          <input type="checkbox" checked={isSuper} onChange={(e) => setIsSuper(e.target.checked)} />
        </label>
      </td>
      <td>
        <label>
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
        </label>
      </td>
      <td>
        <input
          type="password"
          placeholder="New password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <button type="button" className="adm-primary sm" onClick={save}>
          Save
        </button>
        <button type="button" className="adm-ghost sm" onClick={() => setEdit(false)}>
          Cancel
        </button>
      </td>
    </tr>
  );
}

function RoleRow({ r, onSaved, onError, onEditPerms }) {
  const serverDebug = useServerDebug();
  const [edit, setEdit] = useState(false);
  const [name, setName] = useState(r.name);
  const [description, setDescription] = useState(r.description || "");

  const save = async () => {
    onError(null);
    try {
      await api.patch(`/admin/roles/${r.id}`, { name, description });
      setEdit(false);
      await onSaved();
    } catch (e) {
      onError(formatApiError(e, serverDebug));
    }
  };

  const del = async () => {
    if (!window.confirm(`Delete role ${r.name}?`)) return;
    onError(null);
    try {
      await api.delete(`/admin/roles/${r.id}`);
      await onSaved();
    } catch (e) {
      onError(formatApiError(e, serverDebug));
    }
  };

  if (!edit) {
    return (
      <tr>
        <td>{r.name}</td>
        <td>{r.description}</td>
        <td>
          <button type="button" className="adm-link" onClick={() => onEditPerms(r)}>
            Permissions
          </button>{" "}
          <button type="button" className="adm-link" onClick={() => setEdit(true)}>
            Edit
          </button>{" "}
          <button type="button" className="adm-link danger" onClick={del}>
            Delete
          </button>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td>
        <input value={name} onChange={(e) => setName(e.target.value)} />
      </td>
      <td>
        <input value={description} onChange={(e) => setDescription(e.target.value)} />
      </td>
      <td>
        <button type="button" className="adm-primary sm" onClick={save}>
          Save
        </button>
        <button type="button" className="adm-ghost sm" onClick={() => setEdit(false)}>
          Cancel
        </button>
      </td>
    </tr>
  );
}

function PermRow({ p, onSaved, onError }) {
  const serverDebug = useServerDebug();
  const [edit, setEdit] = useState(false);
  const [code, setCode] = useState(p.code);
  const [description, setDescription] = useState(p.description || "");

  const save = async () => {
    onError(null);
    try {
      await api.patch(`/admin/permissions/${p.id}`, { code, description });
      setEdit(false);
      await onSaved();
    } catch (e) {
      onError(formatApiError(e, serverDebug));
    }
  };

  const del = async () => {
    if (!window.confirm(`Delete permission ${p.code}?`)) return;
    onError(null);
    try {
      await api.delete(`/admin/permissions/${p.id}`);
      await onSaved();
    } catch (e) {
      onError(formatApiError(e, serverDebug));
    }
  };

  if (!edit) {
    return (
      <tr>
        <td>
          <code>{p.code}</code>
        </td>
        <td>{p.description}</td>
        <td>
          <button type="button" className="adm-link" onClick={() => setEdit(true)}>
            Edit
          </button>{" "}
          <button type="button" className="adm-link danger" onClick={del}>
            Delete
          </button>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td>
        <input value={code} onChange={(e) => setCode(e.target.value)} />
      </td>
      <td>
        <input value={description} onChange={(e) => setDescription(e.target.value)} />
      </td>
      <td>
        <button type="button" className="adm-primary sm" onClick={save}>
          Save
        </button>
        <button type="button" className="adm-ghost sm" onClick={() => setEdit(false)}>
          Cancel
        </button>
      </td>
    </tr>
  );
}
